import { Box, Button, Container, Flex, Grid, Heading, Stack, Text, Tooltip, useToast } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { Workshop } from 'api/types';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { queryClient } from 'configs/queryClient';
import { useAxios } from 'context/AxiosContextProvider';
import { useUserContext } from 'context/UserContextProvider';
import { createDropdownPlainOptions, createDropdownStaticOptionsWithMutator } from 'helpers/general';
import { formatTelephone, telephonePrefixesOptions, useCountryName } from 'helpers/localization';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import i18n from 'i18n-config';
import { upperCase } from 'lodash';
import { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import AlertModal from '../../components/modals/AlertModal';
import localconfig from '../../config';
import { dropdown, simpleCheckBox, simpleInput } from '../../helpers/makeFormFields';
import { manageErrorResponse } from '../../helpers/manageErrorResponse';
import useDistributorsQuery from '../../hooks/useDistributorsQuery';

export interface IWorkshopUpdateForm {
  workshop_id: number;
}
const FORMFIELD_CONFIG = {
  spacing: 2,
};

export interface IWorkshopUpdateableUserData {
  firstname: string;
  lastname: string;
  email: string;
  billing_email: string;
  telephone: string;
  telephone_prefix: string;
  name: string;
  vat_number: string;
  legal_form: string | null;
  city: string;
  zipcode: string;
  address: string;
  getDataObject: object;
  country: string;
  language: string;
  distributor_id: number | null;
  key_account: number;
}

const getDataObject = (workshop: Workshop | undefined, telephonePrefixes: {}) => {
  const prefix = Object.values(telephonePrefixes).find((countryCode) => workshop?.telephone?.includes(countryCode as string));
  return {
    firstname: workshop?.firstname,
    lastname: workshop?.lastname,
    email: workshop?.email,
    billing_email: workshop?.billing_email,
    telephone_prefix: prefix,
    //@ts-ignore
    telephone: workshop?.telephone?.replace(prefix, ''),
    name: workshop?.name,
    vat_number: workshop?.vat_number,
    legal_form: workshop?.legal_form ?? '',
    city: workshop?.city,
    zipcode: workshop?.zipcode,
    address: workshop?.address,
    distributor_id: workshop?.distributor?.id ?? null,
    disable_cost_control: workshop?.disable_cost_control,
    key_account: workshop?.key_account,
  };
};

export const WorkshopUpdateForm: FC<IWorkshopUpdateForm> = ({ workshop_id }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const axios = useAxios();
  const userContext = useUserContext();
  const distributorsQuery = useDistributorsQuery(1, 100);
  const languages = userContext.config?.languages;
  const telephonePrefixes = userContext.config?.telephone_prefixes;
  const workshopQuery = useWorkshopQuery(workshop_id);
  const countryName = useCountryName(workshopQuery.data?.country);
  const distributors = distributorsQuery.data?.data;
  const getLanguageDropdownOptions = (country: string) => {
    if (!country) {
      return [];
    }
    return createDropdownStaticOptionsWithMutator({
      records: languages[country],
      mutator: (val) => t(`location:languages.${val.toUpperCase()}`),
      t,
    });
  };

  const schema = yup
    .object({
      firstname: yup.string().label(t('workshop:firstname.label')).required(),
      lastname: yup.string().label(t('workshop:lastname.label')).required(),
      email: yup.string().label(t('workshop:email.label')).email().required(),
      billing_email: yup.string().label(t('workshop:billing_email.label')).nullable(),
      country: yup.string().label(t('workshop:country.label')).nullable(),
      language: yup.string().label(t('workshop:language.label')).nullable(),
      name: yup.string().label(t('workshop:name.label')).required(), // Company
      address: yup.string().label(t('workshop:address.label')).required(),
      city: yup.string().label(t('workshop:city.label')).required(),
      zipcode: yup.string().label(t('workshop:zipcode.label')).required(),
      telephone: yup.string().label(t('workshop:telephone.label')).required(),
      telephone_prefix: yup.string().label(t('workshop:telephone.label')).required(),
      distributor_id: yup.string().nullable(),
      disable_cost_control: yup.bool().label(t('workshop:disable_cost_control.label')).required(),
      vat_number: yup
        .string()
        .label(t('workshop:vat_number.label'))
        .transform(upperCase)
        .transform((val) => val.replace(/ /g, '')),
      legal_form: yup.string().label(t('workshop:legal_form.label')).nullable(),
      key_account: yup.bool().label(t('workshop:key_account.label')),
    })
    .required();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid, submitCount },
    watch,
    setError,
    setValue,
    getValues,
    reset,
  } = useForm<IWorkshopUpdateableUserData>({
    // @ts-expect-error
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      getDataObject: getDataObject(workshopQuery.data, telephonePrefixes),

      language: workshopQuery.data?.language,
      key_account: workshopQuery.data?.key_account,
    },
  });

  const chosenDistributorId = watch('distributor_id');
  useEffect(
    // @ts-ignore
    () => reset(getDataObject(workshopQuery.data, telephonePrefixes)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workshopQuery.data],
  );
  const { isPending, mutate } = useMutation<any, Error, IWorkshopUpdateableUserData>({
    mutationFn: async (data: any) => {
      const requestData = {
        ...data,
        telephone: data?.telephone_prefix + data?.telephone,
        country: workshopQuery.data?.country,
        language: data.language === undefined ? workshopQuery.data?.language : data.language,
        distributor_id: chosenDistributorId ?? null,
      };
      // @ts-ignore
      delete requestData.telephone_prefix;
      return await axios
        .put(`${localconfig.apiBaseUrl}/op/v1/workshop/${workshop_id}`, {
          ...requestData,
        })
        .catch((err) => {
          manageErrorResponse(toast, err.response);
          return Promise.reject(err);
        });
    },
  });

  const onSubmit: SubmitHandler<IWorkshopUpdateableUserData> = () => {
    return NiceModal.show(AlertModal as any, {
      onSubmit: () =>
        mutate(getValues(), {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ['workshop', workshop_id],
            });
            navigate(`/app/op/workshops/${workshop_id}`);
          },
          // @ts-ignore
          onError: (error: any) => {
            const { errors, message } = error.response.data;

            if (message) {
              return toast({
                title: message,
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top',
              });
            } else {
              (Object.keys(errors) as Array<keyof IWorkshopUpdateableUserData>).forEach((key) => {
                setError(key, {
                  type: 'server',
                  message: errors[key]!.join('. '),
                });
              });
            }
          },
        }),

      children: t('pages:workshops.update.modal.description'),
      content: {
        header: t('pages:workshops.update.modal.header'),
        footer: {
          buttons: {
            cancelCaption: t('common:abort'),
            actionCaption: t('common:ok'),
          },
        },
      },
    });
  };

  if (workshopQuery.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <Container
        as="form"
        // @ts-expect-error
        onSubmit={handleSubmit(onSubmit)}
        py={{ base: '4', md: '8' }}
        px={0}
        opacity={isPending ? 0.5 : 1}
        data-test-id="update-form"
        centerContent={false}
        m={0}
        maxW="container.lg"
      >
        <Heading size={'xs'}>{t('workshop:form.sections.base_data.label')}</Heading>
        <Stack>
          <Box pl={{ base: 0, lg: 4 }}>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleInput({
                name: 'name',
                label: t('workshop:name.label'),
                register,
                errors,
                schema,
              })}
              {simpleInput({
                name: 'vat_number',
                label: i18n.t('workshop:vat_number.label'),
                register,
                errors,
                schema,
                leftAddon: workshopQuery.data?.country,
                customHook: (val) => {
                  setValue('vat_number', upperCase(val).replace(/ /g, ''));
                },
              })}
            </Stack>
            <Grid gap="6" templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleInput({
                name: 'legal_form',
                label: t('workshop:legal_form.label'),
                register,
                errors,
                schema,
              })}
            </Grid>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleInput({
                name: 'country',
                disabled: true,
                label: t('workshop:country.label'),
                register,
                errors,
                schema,
                value: countryName,
              })}
              {dropdown({
                name: 'language',
                label: t('workshop:language.label'),
                options: getLanguageDropdownOptions(workshopQuery.data?.country || ''),
                control,
                // @ts-ignore
                register,
                errors,
                schema,
                defaultValue: workshopQuery.data?.language.toUpperCase(),
                disabled: getLanguageDropdownOptions(workshopQuery.data?.country || '').length === 1,
              })}
            </Stack>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleInput({
                name: 'city',
                label: t('workshop:city.label'),
                register,
                errors,
                schema,
              })}
              {simpleInput({
                name: 'zipcode',
                label: i18n.t('workshop:zipcode.label'),
                register,
                customHook: (val) => setValue('zipcode', val.replace(/ /g, '')),
                errors,
                schema,
              })}
            </Stack>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleInput({
                name: 'address',
                label: t('workshop:address.label'),
                register,
                errors,
                schema,
              })}
              {dropdown({
                name: 'distributor_id',
                label: t('workshop:distributor.label'),
                options: createDropdownPlainOptions({
                  records: distributors,
                  valueColumn: 'id',
                  labelColumn: 'name',
                  initialOptions: {
                    value: '',
                    label: t('workshop:distributor.no_distributor'),
                  },
                }),
                control,
                // @ts-ignore
                register,
                errors,
                schema,
              })}
            </Stack>
          </Box>

          <Box pl={{ base: 0, lg: 4 }}>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleInput({
                name: 'firstname',
                label: t('workshop:firstname.label'),
                register,
                errors,
                schema,
              })}
              {simpleInput({
                name: 'lastname',
                label: t('workshop:lastname.label'),
                register,
                errors,
                schema,
              })}
            </Stack>

            <Grid gap="6" templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleInput({
                name: 'email',
                label: t('workshop:email.label'),
                register,
                customHook: (val) => {
                  setValue('email', val.replace(/ /g, ''));
                },
                errors,
                schema,
              })}
              <Grid templateColumns={'6rem 1fr'} gap={2} alignItems={'flex-end'}>
                <Box alignSelf={'center'} w={'fit-content'}>
                  <Tooltip label={t('workshop:telephone.info')}>
                    <Text fontSize="sm">{t('workshop:telephone.label')}</Text>
                  </Tooltip>
                  {dropdown({
                    name: 'telephone_prefix',
                    //@ts-ignore
                    control,
                    errors,
                    schema,
                    options: telephonePrefixesOptions(telephonePrefixes, t),
                  })}
                </Box>
                {simpleInput({
                  name: 'telephone',
                  customHook: (val) => setValue('telephone', formatTelephone(val, getValues('telephone_prefix'))),
                  register,
                  errors,
                  schema,
                })}
              </Grid>
            </Grid>
          </Box>

          <hr />
          <Heading size={'xs'}>{t('workshop:form.sections.settings.label')}</Heading>
          <Text>{t('workshop:form.sections.settings.description')}</Text>

          <Box pl={{ base: 0, lg: 4 }}>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleInput({
                name: 'billing_email',
                label: t('workshop:billing_email.label'),
                description: t('workshop:billing_email.info'),
                showAsTooltip: true,
                register,
                errors,
                schema,
              })}
            </Stack>
          </Box>

          <Box pl={{ base: 0, lg: 4 }}>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleCheckBox({
                defaultChecked: workshopQuery.data?.disable_cost_control,
                name: 'disable_cost_control',
                label: t('workshop:disable_cost_control.label'),
                description: t('workshop:disable_cost_control.info'),
                register,
                errors,
                schema,
              })}
            </Stack>
          </Box>
          <Box pl={{ base: 0, lg: 4 }}>
            <Stack spacing="6" direction={{ base: 'column', md: 'row' }} py={FORMFIELD_CONFIG.spacing}>
              {simpleCheckBox({
                defaultChecked: !!workshopQuery.data?.key_account,
                name: 'key_account',
                label: t('workshop:key_account.label'),
                description: t('workshop:key_account.info'),
                register,
                errors,
                schema,
              })}
            </Stack>
          </Box>
        </Stack>
        <Flex direction="row" py="4" justifyContent={'flex-end'}>
          <Button type="submit" variant="primary" disabled={(submitCount! > 0 && !isValid) || isPending} data-test-id="update-submit-button">
            {t('common:submit')}
          </Button>
        </Flex>
      </Container>
    </>
  );
};

export default WorkshopUpdateForm;
